import AssignmentIcon from '@mui/icons-material/Assignment';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  InputAdornment,
  Paper,
  Skeleton,
  Step,
  StepButton,
  StepContent,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useState } from 'react';
import useUnload from '../../../../hooks/useUnload';
import BeaconRte, {
  convertBeaconRteToHtml,
  EmptyRteValue,
  HtmlToBeaconRte,
} from '../../../../shared-components/BeaconRte';
import InstitutionContext from '../../../../shared-components/contexts/InstitutionContext';
import { GetFeatures } from '../Functions/GetFeatures';
import { GetRoles } from '../Functions/GetRoles';
import { GetSteps } from '../Functions/GetSteps';
import { NotationWidgetDispatch, NotationWidgetState } from '../NotationWidget';
import { NotationWidgetMappingService } from '../Services/NotationWidgetMappingService';
import { NotationWidgetService } from '../Services/NotationWidgetService';
import { NotationWidgetActionType } from '../Types/NotationWidgetActionType';
import PermissionTable from './PermissionTable';

interface ISetupPanel {}

const useStyles = makeStyles({
  button: {
    marginTop: '8px',
    marginRight: '10px',
  },
  dimensionInput: {
    maxWidth: '80px',
  },
});

const LoadingContainer = () => {
  return (
    <>
      <Grid item>
        <Skeleton style={{ margin: '8px 0 4px 0' }}>
          <TextField />
        </Skeleton>
      </Grid>
      <Grid item>
        <Skeleton
          variant="rectangular"
          width="100%"
          height="200px"
          style={{ margin: '0 0 0 0' }}
        />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item>
          <Skeleton
            width="80px"
            height="40px"
            style={{ margin: '16px 0 0 0' }}
          />
        </Grid>
        <Grid item>
          <Skeleton
            width="80px"
            height="40px"
            style={{ margin: '16px 0 0 0' }}
          />
        </Grid>
      </Grid>
      <Grid item>
        <Skeleton width="415px" height="20px" />
      </Grid>
    </>
  );
};

const InnerStep = ({ step }: { step: string }) => {
  const classes = useStyles();
  const state = useContext(NotationWidgetState);
  const dispatch = useContext(NotationWidgetDispatch);
  const institutionContext = useContext(InstitutionContext);

  const [openCopyTip, setOpenCopyTip] = useState(false);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  const [rteDesc, setRteDesc] = useState(EmptyRteValue());

  let textAreaValue = `<iframe src="https://${
    //@ts-ignore
    institutionContext.institution['hostName'].value
  }${
    //@ts-ignore
    window.initialAppState.pathContext
  }/widget/Notation" width="${state.width}" height="${
    state.height
  }" frameborder="0"></iframe>`;

  useEffect(() => {
    if (!state.isLoading) {
      setHeight(state.height);
      setWidth(state.width);
      setRteDesc(HtmlToBeaconRte(state.descriptionText));
    }
  }, [state.isLoading]);

  useEffect(() => {
    if (height != state.height && !isNaN(height) && height > 0) {
      dispatch({
        type: NotationWidgetActionType.SetHeight,
        value: height,
      });
    }

    if (width != state.width && !isNaN(width) && width > 0) {
      dispatch({
        type: NotationWidgetActionType.SetWidth,
        value: width,
      });
    }
  }, [height, width]);

  switch (step) {
    case 'Welcome Screen':
      return (
        <Grid container direction="column">
          {state.isLoading ? (
            <LoadingContainer />
          ) : (
            <>
              <Grid item>
                <TextField
                  label="Title Text"
                  defaultValue={state.titleText}
                  margin="dense"
                  onChange={e =>
                    dispatch({
                      type: NotationWidgetActionType.SetTitleText,
                      value: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item style={{ marginTop: '10px' }}>
                <BeaconRte
                  value={rteDesc}
                  label="Description"
                  onChange={e => {
                    setRteDesc(e);
                    dispatch({
                      type: NotationWidgetActionType.SetDescriptionText,
                      value: convertBeaconRteToHtml(e),
                    });
                  }}
                />
              </Grid>
              <Grid item style={{ marginTop: '10px' }}>
                <Grid item container spacing={2}>
                  <Grid item>
                    <TextField
                      label="Height"
                      className={classes.dimensionInput}
                      value={height}
                      type="number"
                      variant="standard"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (isNaN(e.currentTarget.valueAsNumber)) setHeight(0);
                        else setHeight(e.currentTarget.valueAsNumber);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">px</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="Width"
                      className={classes.dimensionInput}
                      value={width}
                      type="number"
                      variant="standard"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (isNaN(e.currentTarget.valueAsNumber)) setWidth(0);
                        else setWidth(e.currentTarget.valueAsNumber);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">px</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid item>
                  <Typography variant="subtitle2">
                    Note: Height and Width will only reflect on the Welcome
                    Screen.
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      );
    case 'Save':
      return (
        <Grid container direction="column">
          <Grid item>
            <Typography>
              To embed the widget in a new location, copy and paste the
              following code.
              <Tooltip
                title="Title, description, and permission will update on all Notation Widgets. Height, width, and legacy widgets will not be updated"
                placement="top"
                arrow
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item>
            <Box width={300}>
              <TextField
                value={textAreaValue}
                multiline
                fullWidth
                onChange={() => {}}
                variant="filled"
                margin="dense"
              />
            </Box>
            <ClickAwayListener onClickAway={() => setOpenCopyTip(false)}>
              <div>
                <Tooltip
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={() => setOpenCopyTip(false)}
                  open={openCopyTip}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  placement="top"
                  title="Copied!"
                  arrow
                >
                  <Button
                    startIcon={<AssignmentIcon />}
                    variant="contained"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(textAreaValue)
                        .then(() => {
                          setOpenCopyTip(true);
                        })
                        .catch(() => {
                          /* Browser does not support navigator */
                        });
                    }}
                  >
                    Copy to Clipboard
                  </Button>
                </Tooltip>
              </div>
            </ClickAwayListener>
          </Grid>
        </Grid>
      );
    default:
      return (
        <PermissionTable
          type={step}
          features={GetFeatures(step)}
          roles={GetRoles()}
        />
      );
  }
};

const SetupPanel = (props: ISetupPanel) => {
  const classes = useStyles();
  const state = useContext(NotationWidgetState);
  const dispatch = useContext(NotationWidgetDispatch);
  const steps = GetSteps();
  const [didFormChange, setDidFormChange] = useState(false);

  const handleStepForward = () => {
    dispatch({
      type: NotationWidgetActionType.SetCurrentStep,
      value: steps[state.currentStep.stepNumber + 1],
    });
  };

  const handleStepBackward = () => {
    dispatch({
      type: NotationWidgetActionType.SetCurrentStep,
      value: steps[state.currentStep.stepNumber - 1],
    });
  };

  const handleNonlinearStep = (step: number) => {
    dispatch({
      type: NotationWidgetActionType.SetCurrentStep,
      value: steps[step],
    });
  };

  useEffect(() => {
    // Handle saving in here when we move on to the 'Finish' step and let the buttons only be responsible for nav
    if (state.currentStep.name === 'Save') {
      if (state.id) {
        NotationWidgetService.UpdateQuickNotation(
          NotationWidgetMappingService.mapDataToQuickNotation(
            state.titleText,
            state.descriptionText,
            state.height,
            state.width,
            state.alertPermissions,
            state.updatePermissions,
            state.encouragementPermissions,
            state.id,
            state.createdById,
            state.createdOn,
          ),
        )
          .then(() => {
            setDidFormChange(false);
          })
          .catch(() => {})
          .finally(() => {});
      } else {
        NotationWidgetService.CreateQuickNotationWidget(
          NotationWidgetMappingService.mapDataToQuickNotation(
            state.titleText,
            state.descriptionText,
            state.height,
            state.width,
            state.alertPermissions,
            state.updatePermissions,
            state.encouragementPermissions,
            state.id,
            state.createdById,
            state.createdOn,
          ),
        )
          .then(() => {
            setDidFormChange(false);
          })
          .catch(() => {})
          .finally(() => {});
      }
    }
  }, [state.currentStep]);

  useEffect(
    () => {
      if (!state.isLoading) {
        setDidFormChange(true);
      }
    },
    //State doesn't seem to pickup when the inner objects change
    [
      state.alertPermissions.showAlerts,
      state.alertPermissions.additionalAudience,
      state.alertPermissions.assignTo,
      state.alertPermissions.audience,
      state.alertPermissions.categories,
      state.alertPermissions.occurenceDate,
      state.alertPermissions.reasons,
      state.encouragementPermissions.showEncouragements,
      state.encouragementPermissions.audience,
      state.updatePermissions.showUpdates,
      state.updatePermissions.additionalAudience,
      state.updatePermissions.audience,
      state.updatePermissions.categories,
      state.updatePermissions.occurenceDate,
      state.updatePermissions.reasons,
      state.descriptionText,
      state.titleText,
      state.width,
      state.height,
    ],
  );

  //NOTE: This needs to be used along side React Routers Prompt to handle
  //      changing between new and old beacon :/
  useUnload((e: any) => {
    e.preventDefault();
    e.returnValue = '';
  }, didFormChange);

  return (
    <>
      <Paper elevation={2}>
        <Box style={{ padding: 30 }}>
          <Typography variant="h4">Setup</Typography>

          <Stepper
            activeStep={state.currentStep.stepNumber}
            orientation="vertical"
          >
            {steps.map((step, index) => (
              <Step key={step.name}>
                <StepButton
                  onClick={() => {
                    handleNonlinearStep(index);
                  }}
                  disabled={state.isLoading}
                  style={{
                    padding: '20px',
                    margin: '-8px',
                    justifyContent: 'flex-start',
                  }}
                >
                  {step.name}
                </StepButton>
                <StepContent>
                  <InnerStep step={step.name} />
                  {state.currentStep.stepNumber > 0 && (
                    <Button
                      onClick={handleStepBackward}
                      className={classes.button}
                    >
                      Back
                    </Button>
                  )}
                  {state.currentStep.name !== 'Save' && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={state.isLoading}
                      onClick={handleStepForward}
                      className={classes.button}
                    >
                      {state.currentStep.stepNumber < steps.length - 2
                        ? 'Next'
                        : 'Save'}
                    </Button>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>
    </>
  );
};
export default SetupPanel;
